import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  ArrowBack,
  EditOutlined,
  DeleteOutline,
  TollOutlined,
  AddCardOutlined,
  AccountBalanceWalletOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { summariseTag } from '../../utils/fastag';
import { toDateString, formatFuelDate, formatRupees } from '../../utils/fuelEfficiency';
import { SummaryTile } from './FastagManager';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';
import AmountField from '../../components/AmountField';

const emptyForm = (type = 'toll') => ({ type, date: new Date(), amount: '', tollPlaza: '', notes: '' });

const FastagDetails = () => {
  const { tagId } = useParams();
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();

  const [tag, setTag] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const detailsTour = useTour('fastag-details', { ready: !loading && !notFound });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteEntry, setDeleteEntry] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [tagResult, entriesResult] = await Promise.all([
      supabase.from('fastags').select('*').eq('id', tagId).eq('user_id', user.id).maybeSingle(),
      supabase
        .from('fastag_entries')
        .select('*')
        .eq('fastag_id', tagId)
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false }),
    ]);

    if (tagResult.error) console.error('Error fetching FASTag:', tagResult.error);
    if (entriesResult.error) {
      console.error('Error fetching FASTag entries:', entriesResult.error);
      notify('Failed to fetch FASTag history', 'error');
    }
    setTag(tagResult.data || null);
    setNotFound(!tagResult.data);
    setEntries(entriesResult.data || []);
    setLoading(false);
  }, [supabase, user, tagId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const summary = useMemo(() => summariseTag(tag, entries), [tag, entries]);

  // ---- Add / edit ----
  const openAddDialog = (type) => {
    setEditingEntry(null);
    setForm(emptyForm(type));
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    const [year, month, day] = entry.date.slice(0, 10).split('-').map(Number);
    setEditingEntry(entry);
    setForm({
      type: entry.entry_type,
      date: new Date(year, month - 1, day),
      amount: String(entry.amount),
      tollPlaza: entry.toll_plaza || '',
      notes: entry.notes || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingEntry(null);
  };

  const handleSave = async () => {
    const amount = parseFloat(form.amount);
    if (!form.date || isNaN(form.date.getTime())) {
      setFormError('Please pick a date');
      return;
    }
    if (!isFinite(amount) || amount <= 0) {
      setFormError('Please enter an amount greater than zero');
      return;
    }
    setSaving(true);
    const entryData = {
      entry_type: form.type,
      date: toDateString(form.date),
      amount,
      toll_plaza: form.type === 'toll' ? form.tollPlaza.trim() : '',
      notes: form.notes.trim(),
    };
    const { error } = editingEntry
      ? await supabase.from('fastag_entries').update(entryData).eq('id', editingEntry.id).eq('user_id', user.id)
      : await supabase.from('fastag_entries').insert({ ...entryData, fastag_id: tagId, user_id: user.id });
    setSaving(false);

    if (error) {
      console.error('Error saving FASTag entry:', error);
      setFormError(`Failed to ${editingEntry ? 'update' : 'save'} entry`);
      return;
    }
    setDialogOpen(false);
    setEditingEntry(null);
    const label = form.type === 'recharge' ? 'Recharge' : 'Toll';
    notify(editingEntry ? `${label} updated successfully` : `${label} added successfully`);
    fetchData();
  };

  const handleDelete = async () => {
    const entry = deleteEntry;
    setDeleteEntry(null);
    const { error } = await supabase.from('fastag_entries').delete().eq('id', entry.id).eq('user_id', user.id);
    if (error) {
      console.error('Error deleting FASTag entry:', error);
      notify('Failed to delete entry', 'error');
      return;
    }
    notify('Entry deleted successfully');
    fetchData();
  };

  // ---- Render ----
  if (loading && !tag) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (notFound) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>FASTag not found</Typography>
          <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate('/utilities/fastag')}>Back to FASTag</Button>
        </Card>
      </Box>
    );
  }

  const balanceColor = summary.balance < 0 ? 'error' : summary.isLow ? 'warning' : 'primary';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/utilities/fastag')} aria-label="Back to FASTag">
            <ArrowBack />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'monospace', letterSpacing: 0.5 }} noWrap>{tag.vehicle_number}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[tag.vehicle_name, tag.issuer, tag.tag_id && `Tag ${tag.tag_id}`].filter(Boolean).join(' · ') || 'FASTag'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} data-tour="fastag-actions">
          <TourButton onClick={detailsTour.start} />
          <Button variant="outlined" color="success" startIcon={<AddCardOutlined />} onClick={() => openAddDialog('recharge')}>Add Recharge</Button>
          <Button variant="contained" startIcon={<TollOutlined />} onClick={() => openAddDialog('toll')}>Add Toll</Button>
        </Box>
      </Box>

      {summary.isLow && !tag.is_archived && (
        <Alert severity={summary.balance < 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
          Balance is below your alert of {formatRupees(tag.low_balance_alert, 0)}. Recharge before your next trip.
        </Alert>
      )}

      <Paper data-tour="fastag-balance" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 1.5 } }}>
        <SummaryTile icon={<AccountBalanceWalletOutlined />} color={balanceColor} value={formatRupees(summary.balance)} label="Current balance" />
        <SummaryTile icon={<TollOutlined />} color="error" value={formatRupees(summary.monthTolls, 0)} label="Tolls this month" />
        <SummaryTile icon={<ReceiptLongOutlined />} color="info" value={formatRupees(summary.tolls, 0)} label={`All tolls (${summary.tollCount})`} />
        <SummaryTile icon={<AddCardOutlined />} color="success" value={formatRupees(summary.recharged, 0)} label="Total recharged" />
      </Paper>

      <Paper data-tour="fastag-history" sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>History</Typography>
        {entries.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No recharges or tolls yet.</Typography>
            <Typography variant="body2" color="text.secondary">
              Opening balance: {formatRupees(tag.opening_balance)}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {entries.map((entry) => {
              const isRecharge = entry.entry_type === 'recharge';
              const title = isRecharge ? 'Recharge' : entry.toll_plaza || 'Toll';
              return (
                <Box
                  key={entry.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: 2,
                    border: '1px solid', borderColor: (t) => alpha(t.palette.divider, 0.6),
                  }}
                >
                  <Box sx={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                    bgcolor: (t) => alpha(isRecharge ? t.palette.success.main : t.palette.error.main, 0.1),
                    color: isRecharge ? 'success.main' : 'error.main',
                    '& svg': { fontSize: 19 },
                  }}>
                    {isRecharge ? <AddCardOutlined /> : <TollOutlined />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{title}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div" noWrap>
                      {[formatFuelDate(entry.date), entry.notes].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: isRecharge ? 'success.main' : 'error.main', fontVariantNumeric: 'tabular-nums' }}>
                    {isRecharge ? '+' : '−'}{formatRupees(entry.amount)}
                  </Typography>
                  <IconButton size="small" aria-label="Edit entry" onClick={() => openEditDialog(entry)}>
                    <EditOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton size="small" aria-label="Delete entry" onClick={() => setDeleteEntry(entry)} sx={{ ml: -0.75, '&:hover': { color: 'error.main' } }}>
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
              Opening balance: {formatRupees(tag.opening_balance)}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Add / edit entry */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Edit' : 'Add'} {form.type === 'recharge' ? 'Recharge' : 'Toll'}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: 1 }}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={form.type}
                onChange={(e, value) => value && setForm({ ...form, type: value })}
              >
                <ToggleButton value="toll" color="error">Toll</ToggleButton>
                <ToggleButton value="recharge" color="success">Recharge</ToggleButton>
              </ToggleButtonGroup>
              <DatePicker
                label="Date *"
                value={form.date}
                onChange={(value) => { setForm({ ...form, date: value }); setFormError(''); }}
                maxDate={new Date()}
                format="dd MMM yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <AmountField
                label="Amount"
                required
                autoFocus
                type="number"
                inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                value={form.amount}
                onChange={(e) => { setForm({ ...form, amount: e.target.value }); setFormError(''); }}
              />
              {form.type === 'toll' && (
                <TextField
                  label="Toll Plaza (Optional)"
                  placeholder="e.g., Khalapur Toll Plaza"
                  value={form.tollPlaza}
                  onChange={(e) => setForm({ ...form, tollPlaza: e.target.value })}
                />
              )}
              <TextField
                label="Notes (Optional)"
                placeholder={form.type === 'toll' ? 'e.g., Mumbai → Pune' : 'e.g., via UPI'}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
              {formError && <Alert severity="error">{formError}</Alert>}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingEntry ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteEntry)} onClose={() => setDeleteEntry(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Entry</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this {deleteEntry?.entry_type === 'recharge' ? 'recharge' : 'toll'} of {formatRupees(deleteEntry?.amount)} on {deleteEntry && formatFuelDate(deleteEntry.date)}? The balance will be updated.
          </DialogContentText>
          {deleteEntry?.toll_plaza && <Chip size="small" label={deleteEntry.toll_plaza} sx={{ mt: 1.5 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEntry(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <GuidedTour steps={TOURS['fastag-details']} open={detailsTour.open} onClose={detailsTour.close} />
    </Box>
  );
};

export default FastagDetails;
